import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Avatar } from "./Avatar";
import { http } from "../lib/api";

const ago = (iso) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "ką tik";
  if (m < 60) return `prieš ${m} min.`;
  if (m < 1440) return `prieš ${Math.floor(m / 60)} val.`;
  return new Date(iso).toLocaleDateString("lt-LT");
};

const who = (n) => `${n.actors[0]?.name || "Kažkas"}${n.count > 1 ? ` ir dar ${n.count - 1}` : ""}`;
const TEXT = {
  comment: (n) => <><b>{who(n)}</b> pakomentavo „{n.title}“: <span className="text-zinc-400">„{n.snippet}“</span></>,
  reply: (n) => <><b>{who(n)}</b> atsakė į jūsų komentarą po „{n.title}“: <span className="text-zinc-400">„{n.snippet}“</span></>,
  follow: (n) => <><b>{who(n)}</b> pradėjo jus sekti</>,
  like_song: (n) => <><b>{who(n)}</b> pamėgo dainą „{n.title}“</>,
  like_playlist: (n) => <><b>{who(n)}</b> pamėgo grojaraštį „{n.title}“</>,
};
const LINK = {
  comment: (n) => `/daina/${n.target_id}`, reply: (n) => `/daina/${n.target_id}`, like_song: (n) => `/daina/${n.target_id}`,
  like_playlist: (n) => `/grojarastis/${n.target_id}`, follow: (n) => `/autorius/${n.actor_id}`,
};

const Item = ({ n, onOpen }) => (
  <button data-testid={`notification-${n.id}`} onClick={() => onOpen(n)}
    className={`w-full flex gap-3 text-left p-3 rounded-xl transition-colors hover:bg-[var(--c-hover)] ${n.read ? "" : "bg-[var(--c-orange)]/5"}`}>
    <Avatar user={n.actors[0]} className="w-9 h-9 text-sm" />
    <div className="min-w-0 flex-1">
      <p className="text-sm leading-snug break-words">{(TEXT[n.type] || TEXT.follow)(n)}</p>
      <p className="text-[11px] text-zinc-500 mt-1">{ago(n.created_at)}</p>
    </div>
    {!n.read && <span data-testid={`notification-unread-dot-${n.id}`} className="w-2 h-2 rounded-full bg-[var(--c-orange)] mt-2 shrink-0" />}
  </button>
);

export const NotificationBell = ({ testId = "notification-bell", align = "start" }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState(null);
  const poll = useCallback(() => http.get("/notifications/unread").then((r) => setUnread(r.data.unread)).catch(() => {}), []);
  useEffect(() => {
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [poll]);
  useEffect(() => {
    if (open) http.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); });
  }, [open]);

  const markRead = async (ids) => {
    await http.post("/notifications/read", { ids });
    setItems((xs) => xs.map((x) => (!ids.length || x.ids.some((i) => ids.includes(i)) ? { ...x, read: true } : x)));
    poll();
  };
  const openItem = (n) => {
    if (!n.read) markRead(n.ids);
    setOpen(false);
    nav((LINK[n.type] || LINK.follow)(n));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testId} title="Pranešimai" className="relative p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-[var(--c-hover)] transition-colors">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid={`${testId}-count`} className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--c-orange)] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-[360px] max-w-[92vw] p-2 bg-[var(--c-elev)] border-[var(--c-line)] text-white rounded-2xl">
        <div className="flex items-center justify-between px-2 py-2">
          <p className="font-display font-semibold">Pranešimai</p>
          {items?.some((x) => !x.read) && (
            <button data-testid="notifications-read-all" onClick={() => markRead([])}
              className="flex items-center gap-1 text-xs text-zinc-400 hover:text-[var(--c-orange)] transition-colors"><CheckCheck className="w-3.5 h-3.5" />Pažymėti visus</button>
          )}
        </div>
        <div data-testid="notifications-list" className="max-h-[420px] overflow-auto scroll-thin space-y-0.5">
          {!items && <div className="h-24 rounded-xl shimmer" />}
          {items?.length === 0 && <p data-testid="notifications-empty" className="text-sm text-zinc-500 text-center py-10">Pranešimų dar nėra.</p>}
          {items?.map((n) => <Item key={n.id} n={n} onOpen={openItem} />)}
        </div>
      </PopoverContent>
    </Popover>
  );
};
